import React, {useContext, useEffect, useMemo, useState} from "react";
import {Fade} from "react-reveal";

import StyleContext from "../../contexts/StyleContext";
import {portfolioShowcase} from "../../portfolio";
import {getProjectStatusMeta, stripStatusTags} from "../../helpers/projectStatus";

// Reuse existing card styling so everything stays consistent
import "../StartupProjects/StartupProjects.scss";
import "./Portfolio.scss";

/**
 * ============================================================
 * PORTFOLIO DATA TEMPLATE (edit: src/portfolio.js)
 * ============================================================
 *
 * export const portfolioShowcase = {
 *   display: true,
 *   title: "Portfolio",
 *   subtitle: "Some things I've built",
 *
 *   // Which tab is selected on first load:
 *   defaultCategory: "all",
 *
 *   // CATEGORIES (tabs)
 *   categories: [
 *     // Example category object:
 *     // { key: "all", label: "All" },
 *     // { key: "games", label: "Games" },
 *     // { key: "tools", label: "Tools" },
 *     // { key: "web", label: "Websites" },
 *     // { key: "jams", label: "Game Jams" },
 *   ],
 *
 *   // ITEMS (cards)
 *   items: [
 *     // Example item for category "games":
 *     // {
 *     //   id: "eternal-dungeons",
 *     //   category: "games", // must match a categories[].key
 *     //   projectName: "Eternal Dungeons",
 *     //   projectDesc: "Multiplayer dungeon game with ...",
 *     //   image: require("./assets/images/ed.png"),
 *     //   gif: require("./assets/images/ed.gif"), // optional (shows on hover)
 *     //   imageScale: 1.0, // optional
 *     //   year: "2025", // optional
 *     //   platform: "PC", // optional
 *     //   role: "Gameplay Programmer", // optional
 *     //   techTags: ["Unity", "Photon", "C#"],
 *     //   footerLink: [
 *     //     { name: "GitHub", url: "https://github.com/..." },
 *     //     { name: "Itch.io", url: "https://...itch.io/..." },
 *     //   ],
 *     // },
 *   ],
 *
 *   // FEATURED (ids from items[])
 *   featuredIds: [
 *     // "eternal-dungeons"
 *   ],
 * };
 *
 * Notes:
 * - Every item.category should match a category key (or it falls back to "other").
 * - Status is taken from your helpers (getProjectStatusMeta / stripStatusTags),
 *   typically via special tags inside techTags (depending on your implementation).
 */

const EMPTY_ARR = Object.freeze([]);
const EMPTY_OBJ = Object.freeze({});

function normalize(s) {
  return (s || "")
    .toString()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function PortfolioCard({item, categoryLabel, isFeatured, isDark}) {
  const [hover, setHover] = useState(false);
  const imageToShow = hover && item.gif ? item.gif : item.image;

  const statusMeta = getProjectStatusMeta(item);
  const visibleTags = stripStatusTags(item.techTags);

  return (
    <div
      className={
        isDark
          ? "dark-mode project-card project-card-dark"
          : "project-card project-card-light"
      }
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{"--status-color": statusMeta?.color || undefined}}
    >
      {/* Image / GIF */}
      {item.image && (
        <div className="project-image">
          <img
            key={imageToShow}
            src={imageToShow}
            alt={item.projectName}
            className="card-image"
            // Force a remount when swapping image <-> gif so GIF playback restarts
            style={
              item.imageScale
                ? {transform: `scale(${item.imageScale})`}
                : undefined
            }
          />
        </div>
      )}

      <div className="project-detail">
        <div className="portfolio-badges">
          {statusMeta && (
            <span
              className="portfolio-badge portfolio-badge--status"
              style={{
                background: `${statusMeta.color}22`,
                border: `1px solid ${statusMeta.color}55`,
                color: isDark ? "#fff" : "#000"
              }}
              aria-label={`Status: ${statusMeta.label}`}
            >
              <span className="status-dot" aria-hidden="true" />
              {statusMeta.icon} {statusMeta.label}
            </span>
          )}

          {categoryLabel && (
            <span className="portfolio-badge portfolio-badge--category">
              {categoryLabel}
            </span>
          )}

          {isFeatured && (
            <span className="portfolio-badge portfolio-badge--featured">
              Featured
            </span>
          )}
        </div>

        {(item.year || item.platform || item.role) && (
          <div className="portfolio-stats" aria-label="Project highlights">
            {item.year && (
              <span className={isDark ? "dark-mode portfolio-stat" : "portfolio-stat"}>
                📅 {item.year}
              </span>
            )}
            {item.platform && (
              <span className={isDark ? "dark-mode portfolio-stat" : "portfolio-stat"}>
                🖥️ {item.platform}
              </span>
            )}
            {item.role && (
              <span
                className={
                  isDark
                    ? "dark-mode portfolio-stat portfolio-stat--role"
                    : "portfolio-stat portfolio-stat--role"
                }
                title={item.role}
              >
                👤 {item.role}
              </span>
            )}
          </div>
        )}

        <h5 className={isDark ? "dark-mode card-title" : "card-title"}>
          {item.projectName}
        </h5>

        <p className={isDark ? "dark-mode card-subtitle" : "card-subtitle"}>
          {item.projectDesc}
        </p>

        {/* Tech tags (status is shown separately) */}
        {visibleTags && visibleTags.length > 0 && (
          <div className="project-card-footer tech-tag-footer">
            {visibleTags.map((tag, i) => (
              <span
                key={`tech-${i}`}
                className={isDark ? "dark-mode tech-tag" : "tech-tag"}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Buttons (Itch.io, Steam, GitHub, etc.) */}
        {item.footerLink && item.footerLink.length > 0 && (
          <div className="project-card-footer project-card-footer--links">
            {item.footerLink.map((link, i) => (
              <span
                key={i}
                className={isDark ? "dark-mode project-btn" : "project-btn"}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(link.url, "_blank");
                }}
              >
                {link.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Portfolio() {
  const {isDark} = useContext(StyleContext);

  // On some mobile browsers (and when "Reduce motion" is enabled), react-reveal
  // can fail to trigger the reveal and keep the section hidden (opacity: 0).
  // We disable the animation in those cases so the Portfolio always renders.
  const [disableReveal, setDisableReveal] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      const isSmall = window.matchMedia("(max-width: 768px)").matches;
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      return isSmall || reduced;
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let mqSmall;
    let mqReduced;

    try {
      mqSmall = window.matchMedia("(max-width: 768px)");
      mqReduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    } catch (e) {
      return undefined;
    }

    const update = () => setDisableReveal(mqSmall.matches || mqReduced.matches);
    update();

    // Safari < 14 uses addListener/removeListener
    const add = (mq, fn) =>
      mq.addEventListener ? mq.addEventListener("change", fn) : mq.addListener(fn);
    const remove = (mq, fn) =>
      mq.removeEventListener ? mq.removeEventListener("change", fn) : mq.removeListener(fn);

    add(mqSmall, update);
    add(mqReduced, update);
    return () => {
      remove(mqSmall, update);
      remove(mqReduced, update);
    };
  }, []);

  // --------------------------------------------------------------------------
  // FIX FOR ESLINT react-hooks/exhaustive-deps WARNINGS
  // We memoize these initializations, so they don't "change every render".
  // --------------------------------------------------------------------------
  const showcase = portfolioShowcase || EMPTY_OBJ;

  const categories = useMemo(
    () => showcase.categories || EMPTY_ARR,
    [showcase.categories]
  );

  const items = useMemo(
    () => showcase.items || EMPTY_ARR,
    [showcase.items]
  );

  const featuredIds = useMemo(
    () => showcase.featuredIds || EMPTY_ARR,
    [showcase.featuredIds]
  );

  const defaultCategory = useMemo(
    () => showcase.defaultCategory || "all",
    [showcase.defaultCategory]
  );

  const featuredSet = useMemo(() => {
    const set = new Set(featuredIds);
    items.forEach((it) => {
      if (it && it.featured === true && it.id) set.add(it.id);
    });
    return set;
  }, [featuredIds, items]);

  const categoryLabelByKey = useMemo(() => {
    const map = new Map();
    categories.forEach((c) => map.set(c.key, c.label));
    return map;
  }, [categories]);

  const counts = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = it.category || "other";
      map.set(key, (map.get(key) || 0) + 1);
    });
    map.set("all", items.length);
    return map;
  }, [items]);

  // Only show category tabs that actually have items.
  // This also hides tabs for disabled/hidden sections since they won't contribute items.
  const visibleCategories = useMemo(() => {
    if (!categories || categories.length === 0) return EMPTY_ARR;
    return categories.filter(
      (c) => c.key === "all" || (counts.get(c.key) || 0) > 0
    );
  }, [categories, counts]);

  const [activeCategory, setActiveCategory] = useState(defaultCategory);
  const [query, setQuery] = useState("");

  // Optional: if you ever change defaultCategory in config, keep state in sync
  useEffect(() => {
    setActiveCategory(defaultCategory);
  }, [defaultCategory]);

  // If the currently selected category disappears (e.g. section disabled), fall back to "all".
  useEffect(() => {
    if (
      activeCategory !== "all" &&
      !visibleCategories.some((c) => c.key === activeCategory)
    ) {
      setActiveCategory("all");
    }
  }, [activeCategory, visibleCategories]);

  const filteredItems = useMemo(() => {
    const q = normalize(query);

    let list = items;

    if (activeCategory !== "all") {
      list = list.filter((it) => (it.category || "other") === activeCategory);
    }

    if (q.length > 0) {
      list = list.filter((it) => {
        const status = getProjectStatusMeta(it);
        const hay = [
          it.projectName,
          it.projectDesc,
          ...(it.techTags || []),
          status?.label,
          status?.key
        ]
          .map(normalize)
          .join(" ");

        return hay.includes(q);
      });
    }

    // Featured first, then most recent (if provided), then name
    return [...list].sort((a, b) => {
      const af = featuredSet.has(a.id) ? 1 : 0;
      const bf = featuredSet.has(b.id) ? 1 : 0;
      if (af !== bf) return bf - af;

      const ay = Number(a.year || 0);
      const by = Number(b.year || 0);
      if (ay !== by) return by - ay;

      return (a.projectName || "").localeCompare(b.projectName || "");
    });
  }, [activeCategory, featuredSet, items, query]);

  const featuredItems = useMemo(() => {
    const byId = new Map(items.map((it) => [it.id, it]));
    const fromIds = featuredIds.map((id) => byId.get(id)).filter(Boolean);

    const featuredIdSet = new Set(featuredIds);
    const auto = items
      .filter(
        (it) => it && it.featured === true && it.id && !featuredIdSet.has(it.id)
      )
      .slice()
      .sort((a, b) => {
        const ao = typeof a.featuredOrder === "number" ? a.featuredOrder : 9999;
        const bo = typeof b.featuredOrder === "number" ? b.featuredOrder : 9999;
        if (ao !== bo) return ao - bo;
        return String(a.projectName || "").localeCompare(String(b.projectName || ""));
      });

    return [...fromIds, ...auto];
  }, [items, featuredIds]);

  // After all hooks have run, it's safe to bail out.
  if (!showcase.display) return null;

  return (
    <Fade bottom duration={1000} distance="20px" disabled={disableReveal}>
      <div className="main" id="portfolio">
        <div>
          <div className="portfolio-header">
            <h1 className="skills-heading">{showcase.title}</h1>

            {showcase.subtitle && (
              <p
                className={
                  isDark
                    ? "dark-mode project-subtitle"
                    : "subTitle project-subtitle"
                }
              >
                {showcase.subtitle}
              </p>
            )}
          </div>

          {/* Featured banner */}
          {featuredItems.length > 0 && (
            <div className={isDark ? "portfolio-featured dark-mode" : "portfolio-featured"}>
              <div className="portfolio-featured-head">
                <div>
                  <h2
                    className={
                      isDark
                        ? "dark-mode portfolio-featured-title"
                        : "portfolio-featured-title"
                    }
                  >
                    Featured projects
                  </h2>
                </div>
              </div>

              <div className="portfolio-featured-grid">
                {featuredItems.slice(0, 4).map((item) => {
                  const label = categoryLabelByKey.get(item.category) || "Other";
                  const isFeatured = featuredSet.has(item.id);
                  return (
                    <PortfolioCard
                      key={`featured-${item.id}`}
                      item={item}
                      categoryLabel={label}
                      isFeatured={isFeatured}
                      isDark={isDark}
                    />
                  );
                })}
              </div>
            </div>
          )}

          <div className={isDark ? "portfolio-controls dark-mode" : "portfolio-controls"}>
            <div className="portfolio-tabs" role="tablist" aria-label="Portfolio categories">
              {/* Each tab below comes from portfolioShowcase.categories[] */}
              {visibleCategories.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={
                    activeCategory === c.key
                      ? "portfolio-tab portfolio-tab--active"
                      : "portfolio-tab"
                  }
                  onClick={() => setActiveCategory(c.key)}
                >
                  {c.label}
                  <span className="portfolio-tab-count">
                    {counts.get(c.key) || 0}
                  </span>
                </button>
              ))}
            </div>

            <div className="portfolio-search">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title, description, or tag…"
                aria-label="Search portfolio"
              />
              {query && (
                <button
                  type="button"
                  className="portfolio-clear"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {filteredItems.length === 0 ? (
            <div className={isDark ? "portfolio-empty dark-mode" : "portfolio-empty"}>
              <h3>No matches</h3>
              <p>Try a different category or search term.</p>
            </div>
          ) : (
            <div className="projects-container">
              {filteredItems.map((item) => {
                const label = categoryLabelByKey.get(item.category) || "Other";
                const isFeatured = featuredSet.has(item.id);
                return (
                  <PortfolioCard
                    key={item.id}
                    item={item}
                    categoryLabel={label}
                    isFeatured={isFeatured}
                    isDark={isDark}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Fade>
  );
}
